import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getUnlistedDPMaping } from "../../redux/rm/rmSlice";
import toast from "react-hot-toast";
import { FileDownload, Close } from "@mui/icons-material";
import AppLoader from "../../components/AppLoader/AppLoader";
import DocumentModal from "../../components/Modal/Unlisted/DocumentModal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

const UnlistedDPMapping = () => {
  const dispatch = useDispatch<any>();
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  // Modals
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [documentUrls, setDocumentUrls] = useState<any>();
  const [userData, setUserData] = useState<any>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    bankAccNo: "",
    ifscCode: "",
    bankName: "",
  });

  // Date Range
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  /* --------------------------------------------------------------- */
  /*  FETCH DATA                                                    */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    fetchUnlistedInvestors();
  }, []);

  useEffect(() => {
    filterDataByDateRange();
  }, [startDate, endDate, allUsers, currentPage, pageSize]);

  const fetchUnlistedInvestors = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getUnlistedDPMaping());
      if (response?.error?.message) {
        toast.error(response?.payload?.message || "Failed to fetch data");
        setAllUsers([]);
        setFilteredUsers([]);
        setTotalPages(0);
        return;
      }
      const data = response?.payload?.data || [];
      setAllUsers(data);
    } catch (error) {
      console.error("Error fetching investors:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------------------- */
  /*  FILTER & PAGINATION                                            */
  /* --------------------------------------------------------------- */
  const filterDataByDateRange = () => {
    let filtered = allUsers;

    if (startDate && endDate) {
      const startOfDay = startDate.startOf("day");
      const endOfDay = endDate.endOf("day");

      filtered = allUsers.filter((user) => {
        const createdAt = dayjs(user.createdAt);
        const afterOrSameStart =
          createdAt.isAfter(startOfDay) || createdAt.isSame(startOfDay);
        const beforeOrSameEnd =
          createdAt.isBefore(endOfDay) || createdAt.isSame(endOfDay);
        return afterOrSameStart && beforeOrSameEnd;
      });
    }

    const total = Math.ceil(filtered.length / pageSize);
    setTotalPages(total);

    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    setFilteredUsers(filtered.slice(start, end));
  };

  const onPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => setCurrentPage(newPage + 1);

  const onPageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  /* --------------------------------------------------------------- */
  /*  EXPORT TO .TXT – ONLY HEADER + DP MAPPING LINES                */
  /* --------------------------------------------------------------- */
  const exportToTxt = () => {
    // Choose data set
    const dataToExport = startDate && endDate ? filteredUsers : allUsers;

    // Today’s date in YYYYMMDD
    const today = dayjs().format("YYYYMMDD"); // e.g. 20251031

    // 1. Header line (always first)
    // 01#|IN304211#|00000007#|20251013#|00000202
    // const header = `01#|IN304211#|00000007#|${today}#|00000202`;

    // 2. Normalize DPMapping strings to a single line and remove embedded EOLs
    // Some viewers on Windows (e.g., Notepad) require CRLF (\r\n) to render new lines.
    // Also, source data may sometimes include stray \n/\r—strip them to keep one mapping per line.
    const sanitize = (s: string) =>
      String(s || "")
        .replace(/\r\n|\r|\n/g, "")
        .trim();

    const dpLines = dataToExport
      .map((u) => sanitize(u.DPMapping))
      .filter((line) => line !== "");
    const countStr = String(dpLines.length).padStart(8, "0");
    const header = `01#|IN304211#|${countStr}#|${today}#|00000202`;
    // 3. Final file content with Windows-friendly CRLF newlines (+ final EOL)
    const EOL = "\r\n";
    const txtContent = [header, ...dpLines].join(EOL) + EOL;

    // 4. Download
    const blob = new Blob([txtContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `DP_Mapping_${today}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDateToDDMMYYYY = (isoString: string) => {
    const d = new Date(isoString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  /* --------------------------------------------------------------- */
  /*  MENU & MODALS                                                  */
  /* --------------------------------------------------------------- */
  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, user: any) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedUser(user);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleDocuments = (user: any) => {
    setDocumentUrls({
      cmlDoc: user.cmlDoc,
      panDoc: user.panDoc,
      cancelledChequeDoc: user.cancelledChequeDoc,
    });
    setUserData(user);
    setDocumentModalOpen(true);
    handleMenuClose();
  };
  const handleDocumentModalClose = () => {
    setDocumentModalOpen(false);
    setDocumentUrls(null);
  };

  const handleAddBankAccount = () => {
    setBankDetails({ bankAccNo: "", ifscCode: "", bankName: "" });
    setBankModalOpen(true);
    handleMenuClose();
  };
  const handleBankModalClose = () => setBankModalOpen(false);
  const handleBankDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({ ...prev, [name]: value }));
  };
  const handleBankSubmit = () => {
    if (
      !bankDetails.bankAccNo ||
      !bankDetails.ifscCode ||
      !bankDetails.bankName
    ) {
      toast.error("Please fill all bank details");
      return;
    }
    toast.success("Bank details submitted successfully!");
    setBankModalOpen(false);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (startDate && newValue && newValue.isBefore(startDate)) {
      toast.error("End date cannot be before start date");
      return;
    }
    setEndDate(newValue);
    setCurrentPage(1);
  };

  /* --------------------------------------------------------------- */
  /*  RENDER                                                         */
  /* --------------------------------------------------------------- */
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <AppLoader loading={loading} />
      <div
        className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        {/* Header */}
        <Box
          width="90%"
          mb={2}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">Investor DP Mapping</Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownload />}
            onClick={exportToTxt}
            sx={{ textTransform: "none", borderRadius: "8px", px: 3, py: 1 }}
          >
            Export Data
          </Button>
        </Box>

        {/* Date Range */}
        <Box
          width="90%"
          mb={2}
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(v) => {
              setStartDate(v);
              setCurrentPage(1);
            }}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndDateChange}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
          />
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
              setCurrentPage(1);
            }}
          >
            Clear
          </Button>
        </Box>

        {/* Table */}
        <div
          style={{ display: "flex", justifyContent: "center", width: "90%" }}
        >
          <TableContainer
            component={Paper}
            style={{ width: "100%", marginTop: "1rem" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Phone No.
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Created At
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    DP Mapping
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {allUsers.length === 0
                          ? "No data available"
                          : "No data in selected date range"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.clientId || user.dbId}>
                      <TableCell>{user.clientName || "-"}</TableCell>
                      <TableCell>{user.phone || "-"}</TableCell>
                      <TableCell>
                        {user.createdAt
                          ? formatDateToDDMMYYYY(user.createdAt)
                          : "-"}
                      </TableCell>
                      <TableCell
                        style={{
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                        }}
                      >
                        {user.DPMapping || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    count={allUsers.length}
                    rowsPerPage={pageSize}
                    page={currentPage - 1}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onPageSizeChange}
                    labelDisplayedRows={() => `${currentPage} of ${totalPages}`}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>

        {/* More Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => handleDocuments(selectedUser)}>
            Documents
          </MenuItem>
          <MenuItem onClick={handleAddBankAccount}>Add Bank Account</MenuItem>
        </Menu>

        {/* Bank Modal */}
        <Modal open={bankModalOpen} onClose={handleBankModalClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 2,
              boxShadow: 24,
            }}
          >
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Add Bank Account</Typography>
              <IconButton onClick={handleBankModalClose}>
                <Close />
              </IconButton>
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Bank Account No"
                name="bankAccNo"
                value={bankDetails.bankAccNo}
                onChange={handleBankDetailsChange}
                fullWidth
              />
              <TextField
                label="IFSC Code"
                name="ifscCode"
                value={bankDetails.ifscCode}
                onChange={handleBankDetailsChange}
                fullWidth
              />
              <TextField
                label="Bank Name"
                name="bankName"
                value={bankDetails.bankName}
                onChange={handleBankDetailsChange}
                fullWidth
              />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button variant="outlined" onClick={handleBankModalClose}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleBankSubmit}>
                  Submit
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Document Modal */}
        <DocumentModal
          open={documentModalOpen}
          handleClose={handleDocumentModalClose}
          documentUrls={documentUrls}
          userData={userData}
          fetchUnlistedInvestors={fetchUnlistedInvestors}
        />
      </div>
    </LocalizationProvider>
  );
};

export default UnlistedDPMapping;
